import React, { useState } from "react";
import { Modal, Button, Alert, Spinner, ProgressBar } from "react-bootstrap";

import { singleAvatarImport } from "../../api/avatar";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

interface UploadPhotoModalProps {
  profilId: string | null | undefined;
  profilType: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadPhotoModal: React.FC<UploadPhotoModalProps> = ({
  profilId,
  profilType,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setPreview(null);
    setFile(null);
    setProgress(0);
    setError(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    setError(null);

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];

    if (!validTypes.includes(selectedFile.type)) {
      setError("Format non supporté. Utilisez JPEG, PNG, GIF ou WebP.");
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      setError("Fichier trop volumineux. Maximum 5 MB.");
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();

    reader.onload = (e) => {
      setPreview(e.target?.result as string);
    };

    reader.readAsDataURL(selectedFile);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.add("dragging");
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.currentTarget.classList.remove("dragging");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.classList.remove("dragging");

    if (e.dataTransfer.files?.[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!file || !profilId) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();

      formData.append("entity_type", profilType);
      formData.append("entity_id", profilId);
      formData.append("avatar", file);

      await singleAvatarImport(
        formData,
        buildPermissionContext(Resource.STUDENT, Action.UPDATE),
      );

      setProgress(100);

      onSuccess();
      onClose();

      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'upload");
    } finally {
      setLoading(false);
      setProgress(0);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header className="bg-light">
        <Modal.Title>Ajouter une photo</Modal.Title>

        <Button
          variant="danger"
          size="sm"
          className="rounded"
          onClick={onClose}
          disabled={loading}
        >
          <i className="fa-solid fa-xmark" />
        </Button>
      </Modal.Header>

      <Modal.Body className="p-4">
        {error && <Alert variant="danger">{error}</Alert>}

        {!preview ? (
          <div
            className="upload-area"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-icon">📷</div>

            <div className="upload-text">Déposer une image ici</div>

            <div className="upload-subtext">
              ou cliquez pour sélectionner (Max 5 MB)
            </div>
          </div>
        ) : (
          <div className="upload-preview text-center">
            <img src={preview} alt="Aperçu" className="img-fluid rounded" />
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          className="d-none"
          accept="image/*"
          onChange={handleFileChange}
          disabled={loading}
        />

        {preview && (
          <div className="mt-3">
            <Button
              variant="link"
              className="w-100"
              onClick={() => {
                resetForm();
                fileInputRef.current?.click();
              }}
              disabled={loading}
            >
              Changer l'image
            </Button>
          </div>
        )}

        {loading && (
          <div className="text-center mt-4">
            <Spinner animation="border" variant="primary" className="mb-3" />

            <div className="text-muted mb-2">
              Upload en cours... {Math.round(progress)}%
            </div>

            <ProgressBar now={progress} animated striped />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex gap-2">
        <Button variant="danger" onClick={onClose} disabled={loading}>
          Fermer
        </Button>

        {preview && (
          <Button variant="primary" onClick={handleUpload} disabled={loading}>
            {loading ? "Upload en cours..." : "Télécharger"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default UploadPhotoModal;
